import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Search, Filter, UserCog, CheckCircle } from 'lucide-react'

interface User {
  id: string
  email: string
  display_name: string | null
  role: 'buyer' | 'provider' | 'admin'
  created_at: string
  updated_at: string
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showRoleModal, setShowRoleModal] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    filterUsers()
  }, [users, searchTerm, roleFilter])

  const fetchUsers = async () => {
    try {
      // Use admin RPC function to bypass RLS
      const { data, error } = await supabase
        .rpc('admin_get_all_profiles')

      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterUsers = () => {
    let filtered = users

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.role === roleFilter)
    }

    setFilteredUsers(filtered)
  }

  const handleChangeRole = async (userId: string, newRole: 'buyer' | 'provider' | 'admin') => {
    try {
      // Use admin RPC function to update role
      const { error } = await supabase
        .rpc('admin_update_user_role', {
          user_id: userId,
          new_role: newRole
        })

      if (error) throw error

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.id === userId ? { ...user, role: newRole } : user))
      )
      setShowRoleModal(false)
      setSelectedUser(null)
    } catch (error) {
      console.error('Error updating user role:', error)
      alert(error instanceof Error ? error.message : 'Failed to update user role')
    }
  }

  const getRoleBadge = (role: string) => {
    const badges = {
      admin: 'admin-badge-danger',
      provider: 'admin-badge-info',
      buyer: 'admin-badge-success',
    }
    return badges[role as keyof typeof badges] || 'admin-badge'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <p className="text-gray-600 mt-1">View and manage platform users</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Total Users</p>
          <p className="text-2xl font-bold">{users.length}</p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Admins</p>
          <p className="text-2xl font-bold">
            {users.filter((u) => u.role === 'admin').length}
          </p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Providers</p>
          <p className="text-2xl font-bold">
            {users.filter((u) => u.role === 'provider').length}
          </p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Buyers</p>
          <p className="text-2xl font-bold">
            {users.filter((u) => u.role === 'buyer').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 w-5 h-5" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="provider">Provider</option>
              <option value="buyer">Buyer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Display Name</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-admin-primary flex items-center justify-center text-white text-xs font-medium">
                          {user.email[0].toUpperCase()}
                        </div>
                        {user.email}
                      </div>
                    </td>
                    <td>{user.display_name || '-'}</td>
                    <td>
                      <span className={getRoleBadge(user.role)}>
                        {user.role}
                      </span>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => {
                          setSelectedUser(user)
                          setShowRoleModal(true)
                        }}
                        className="text-admin-primary hover:text-admin-secondary font-medium text-sm flex items-center gap-1"
                      >
                        <UserCog className="w-4 h-4" />
                        Change Role
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Change User Role</h3>
            <p className="text-gray-600 text-sm mb-4">
              Change role for <strong>{selectedUser.email}</strong>
            </p>

            <div className="space-y-2 mb-6">
              {(['buyer', 'provider', 'admin'] as const).map((role) => (
                <button
                  key={role}
                  onClick={() => handleChangeRole(selectedUser.id, role)}
                  className={`w-full p-3 border rounded-md text-left hover:bg-gray-50 transition-colors ${
                    selectedUser.role === role ? 'border-admin-primary bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium capitalize">{role}</p>
                      <p className="text-xs text-gray-500">
                        {role === 'admin' && 'Full platform access'}
                        {role === 'provider' && 'Can mint and sell credits'}
                        {role === 'buyer' && 'Can buy and retire credits'}
                      </p>
                    </div>
                    {selectedUser.role === role && (
                      <CheckCircle className="w-5 h-5 text-admin-primary" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowRoleModal(false)
                  setSelectedUser(null)
                }}
                className="flex-1 admin-button-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
